<template>
  <div class="check-input-wrapper">
    <label :class="[{ '-disabled': disabled, '-optional': cptdIsOptional }]">
      {{ label }}
      <span v-if="cptdIsOptional">Opcional</span>
      <label v-else class="required">&nbsp;*</label>
    </label>
    <div class="options-list" :class="{ vertical: vertical }">
      <label
        v-for="option in options"
        :key="option.value"
        class="option-wrapper"
      >
        <input
          v-model="value"
          class="invisible-input"
          type="radio"
          :value="option.value"
          :name="label"
        />
        <button
          class="option"
          :class="[
            {
              checked: value === option.value,
              'has-icon': option.icon,
              binary: binary,
            },
            { desktop: !vertical },
            option.selectColor,
          ]"
          @click="setVal(option.value)"
        >
          <span
            v-if="option.icon"
            class="option--icon"
            @click="setVal(option.value)"
          >
            <Icon
              :size="option.icon.size"
              :name="option.icon.name"
              :color="option.icon.color"
            />
          </span>
          <p class="option--text">{{ option.label }}</p>
        </button>
      </label>
    </div>
    <div class="validation">
      <p
        v-if="(validation || meta.validated) && errorMessage"
        class="error-message"
      >
        {{ errorMessage }}
      </p>
      <p v-if="helperMessage" class="helper-message">
        {{ helperMessage }}
      </p>
    </div>
  </div>
</template>
<script>
import Icon from '@/components/ui/Icon.vue'
import { toRef } from 'vue'
import { useField } from 'vee-validate'
export default {
  name: 'CheckInput',
  components: {
    Icon,
  },
  props: {
    id: { type: String, required: true },
    binary: { type: Boolean, default: false },
    description: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    helperMessage: { type: String, default: '' },
    label: { type: String, default: '' },
    options: { type: Array, required: true },
    validations: {
      type: [Object, String],
      default: 'required',
    },
    validation: { type: Boolean, default: false },
    modelValue: { type: [String, Number, Boolean], default: '' },
    vertical: { type: Boolean, default: false },
  },
  emits: ['input', 'update:modelValue'],
  setup(props) {
    // use `toRef` to create reactive references to `name` prop which is passed to `useField`
    // this is important because vee-validte needs to know if the field name changes
    // https://vee-validate.logaretm.com/v4/guide/composition-api/caveats
    const name = toRef(props, 'id')
    const rules = toRef(props, 'validations')

    const { errorMessage, setTouched, handleChange, meta, value, resetField } =
      useField(name, rules, {
        syncVModel: true,
      })

    return {
      handleChange,
      setTouched,
      errorMessage,
      meta,
      value,
      resetField,
    }
  },
  data() {
    return {
      errors: [],
    }
  },
  computed: {
    cptdIsOptional() {
      return !this.validations || this.validations?.required === false
    },
  },
  mounted() {
    if (this.value) {
      this.setTouched(true)
    }
  },
  methods: {
    setVal(option) {
      this.setTouched(true)
      this.handleChange(option)
      this.$emit('input', option)
    },
  },
}
</script>
<style lang="scss">
.check-input-wrapper {
  margin-bottom: 24px;

  & > label {
    @include subtitle-1;
    color: $color-black-high;
    margin-bottom: 11px;

    &.-optional {
      display: flex;
      align-items: center;
      justify-content: space-between;

      & > span {
        @include body-2;
        color: $color-black-medium;
      }
    }

    &.-disabled {
      color: $color-brand-tertiary;
    }

    & > .required {
      color: $color-feedback-negative;
    }
  }

  & > .options-list {
    margin-top: 16px;
    display: flex;
    flex-direction: row;
    flex: 1 1 100%;

    & > .option-wrapper {
      width: 100%;
      max-width: 50%;
    }
  }

  & > .validation {
    & > .error-message {
      @include caption;
      margin: 8px 0 0 0;
      display: block;
      color: $color-feedback-negative;
    }

    & > .helper-message {
      @include caption;
      margin: 8px 0 0 0;
      color: $color-black-medium;
    }
  }

  .options-list.vertical {
    width: 100%;
    flex-direction: column;

    .option--text {
      text-align: left;
    }
  }

  .options-list.vertical .option {
    margin: 4px 0;
    border-radius: 5px;
  }

  .options-list label:first-child .option {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  .options-list label:last-child .option {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  .option {
    background-color: $color-white-high;
    color: $color-brand-primary-dark;
    padding: 16px;
    transition:
      background-color,
      color,
      border-color 200ms linear;
    cursor: pointer;
    width: 100%;
    margin: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    height: 48px;
    border: 1px solid $color-neutral-dark;
  }

  .option.binary {
    justify-content: center;
  }

  .option--text {
    @include body-1;
    width: 100%;
    text-align: center;
  }

  .option.checked,
  .option:hover {
    background-color: rgba(84, 32, 164, 0.08);
    color: $color-brand-primary-dark;
    border-color: $color-brand-primary;
    transition:
      background-color,
      color,
      border-color 200ms linear;
  }

  .option.red.checked,
  .option.red:hover {
    background-color: $color-feedback-negative-light;
    color: $color-feedback-negative;
    border-color: $color-feedback-negative;
    transition:
      background-color,
      color,
      border-color 200ms linear;
  }

  .option.has-icon {
    padding-left: 0;
  }

  .option--icon {
    margin-right: 10px;
    border-right: 1px solid $color-neutral-dark;
    padding: 0 25px;
    height: 52px;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    position: relative;
    transition:
      color,
      border-color 200ms linear;
  }

  .option.binary .option--icon {
    margin-right: 0;
    border: 0;
  }

  .option--icon svg {
    max-width: 25px;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    margin: auto;
  }

  .option:hover .option--icon,
  .option.checked .option--icon {
    border-color: $color-feedback-positive;
    color: $color-feedback-positive;
    transition:
      color,
      border-color 200ms linear;
  }

  .option.red:hover .option--icon,
  .option.red.checked .option--icon {
    border-color: $color-feedback-negative;
    transition:
      color,
      border-color 200ms linear;
  }

  .option:hover .option--icon svg,
  .option.checked .option--icon svg {
    fill: $color-feedback-positive;
    transition:
      color,
      border-color 200ms linear;
  }

  .option.red:hover .option--icon svg,
  .option.red.checked .option--icon svg {
    fill: $color-feedback-negative;
    transition:
      color,
      border-color 200ms linear;
  }

  .invisible-input {
    display: none;
  }
}
</style>
